import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarDays, ChevronRight, Sparkles, Users } from "lucide-react";
import { GraduateWith, SyllabusRoadmap } from "@/components/flagship/SyllabusRoadmap";

export default function Flagship() {
  const [data, setData] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponMeta, setCouponMeta] = useState(null);
  const { user } = useAuth();
  const { currency, format } = useCurrency();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/catalog/flagship").then((r) => {
      setData(r.data);
      if (r.data.cohorts?.length) setCohort(r.data.cohorts[0]);
    });
  }, []);

  if (!data) return <div className="min-h-screen bg-[#09090B] text-zinc-400 flex items-center justify-center">Loading…</div>;

  const price = data[`price_${currency}`];
  const finalPrice = couponMeta ? Math.max(0, price - couponMeta.discount) : price;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data: c } = await api.post("/coupons/validate", { code: coupon, subtotal: price, currency });
      setCouponMeta(c);
      toast.success(`Coupon applied · ${format(c.discount)} off`);
    } catch (e) {
      setCouponMeta(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const enrol = async () => {
    if (!cohort) return toast.error("Pick a cohort first");
    if (!user) { nav("/login?next=/flagship"); return; }
    try {
      const { data: r } = await api.post("/payments/checkout", {
        item_kind: "flagship_cohort", item_id: cohort.cohort_id, origin_url: window.location.origin,
        currency, coupon_code: couponMeta ? coupon : undefined,
      });
      if (r.provider === "razorpay") {
        const { openRazorpayCheckout } = await import("@/lib/razorpay");
        await openRazorpayCheckout({
          orderData: r,
          onSuccess: async (resp) => {
            try {
              await api.post("/payments/razorpay/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              window.location.href = `/payment/return?session_id=${r.session_id}`;
            } catch (err) { toast.error("Payment verification failed. Contact support with your payment ID."); }
          },
          onError: () => toast.error("Payment failed. Please try again."),
        });
      } else {
        window.location.href = r.checkout_url;
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grid-bg-adult" />
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-5 gap-10 relative items-start">
          <div className="md:col-span-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-3 py-1 text-xs uppercase tracking-[0.2em] font-adult-mono text-amber-300">
              <Sparkles className="w-3 h-3"/>Flagship · Live
            </div>
            <h1 className="mt-6 font-adult-heading text-5xl md:text-6xl font-semibold tracking-tight leading-[1.02]">
              {data.title.split("—")[0]}<br/>
              <span className="font-adult-serif italic text-amber-400">— {data.title.split("—")[1]}</span>
            </h1>
            <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-2xl">{data.description}</p>
            <div className="mt-6 grid grid-cols-3 gap-4 max-w-xl">
              <Stat label="Duration" value={`${data.duration_weeks} weeks`} />
              <Stat label="Sessions" value={`${data.sessions_per_week}/week`} />
              <Stat label="Days" value={data.days} />
            </div>
          </div>
          <div className="md:col-span-2 md:sticky md:top-24">
            <div className="rounded-3xl border border-zinc-800 bg-zinc-950 p-6 glow-amber">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">Enrol in a batch</div>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-adult-mono text-4xl font-semibold text-amber-400">{format(finalPrice)}</div>
                {couponMeta && <div className="text-sm text-zinc-500 line-through font-adult-mono">{format(price)}</div>}
              </div>

              <div className="mt-6 space-y-2">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">Cohort</div>
                {data.cohorts.map((c) => {
                  const full = c.seats_taken >= c.capacity;
                  const daysUntil = Math.max(0, Math.ceil((new Date(c.start_date_iso) - Date.now()) / 86400000));
                  return (
                    <button
                      key={c.cohort_id}
                      data-testid={`cohort-${c.cohort_id}`}
                      onClick={() => !full && setCohort(c)}
                      disabled={full}
                      className={`w-full text-left rounded-lg border p-3 transition-smooth ${cohort?.cohort_id===c.cohort_id ? "border-amber-500 bg-amber-500/10" : "border-zinc-800 hover:border-zinc-600"} ${full ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium">{c.name}</div>
                        {full ? (
                          <span className="text-[10px] font-adult-mono uppercase tracking-[0.1em] text-red-400 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5">Full</span>
                        ) : daysUntil > 0 ? (
                          <span className="text-[10px] font-adult-mono uppercase tracking-[0.1em] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"/>{daysUntil}d
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-zinc-500 font-adult-mono mt-1 flex items-center gap-2">
                        <CalendarDays className="w-3 h-3"/>{new Date(c.start_date_iso).toLocaleDateString(undefined,{month:"short", day:"numeric", year:"numeric"})}
                        <Users className="w-3 h-3 ml-1"/>{c.capacity - c.seats_taken} of {c.capacity} left
                      </div>
                      {!full && daysUntil > 0 && daysUntil <= 30 && (
                        <div className="mt-2 text-[10px] font-adult-mono uppercase tracking-[0.15em] text-amber-400 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"/>
                          Starts in {daysUntil} day{daysUntil === 1 ? "" : "s"} — enrol now
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-adult-mono mb-2">Have a coupon?</div>
                <div className="flex gap-2">
                  <Input data-testid="flagship-coupon-input" placeholder="LAUNCH10" value={coupon} onChange={(e)=>setCoupon(e.target.value.toUpperCase())} className="h-10 bg-zinc-900 border-zinc-800 font-adult-mono"/>
                  <Button data-testid="flagship-coupon-apply" variant="outline" onClick={applyCoupon} className="h-10 border-zinc-800 hover:border-amber-500/60">Apply</Button>
                </div>
                {couponMeta && <div className="text-xs text-emerald-400 mt-2 font-adult-mono">✓ {couponMeta.code} applied · saved {format(couponMeta.discount)}</div>}
              </div>

              <Button data-testid="flagship-enrol" onClick={enrol} className="w-full mt-6 h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold text-base glow-amber">
                Enrol now <ChevronRight className="w-4 h-4 ml-1"/>
              </Button>
              <div className="mt-3 text-[11px] text-zinc-500 text-center font-adult-mono">Seat locked until first-week refund window</div>
            </div>
          </div>
        </div>
      </section>

      <SyllabusRoadmap syllabus={data.syllabus} />
      <GraduateWith outcomes={data.outcomes} />
      <Footer variant="adult" />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-zinc-800 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">{label}</div>
      <div className="mt-1 font-adult-heading text-lg">{value}</div>
    </div>
  );
}
